import React from "react";
import ifElse from "ramda/src/ifElse";
import always from "ramda/src/always";
import pathOr from "ramda/src/pathOr";
import compose from "ramda/src/compose";
import { useHistory } from "react-router";

import { Alert } from "../../UI/Alert";
import { Grid } from "../../UI/Grid";
import { UserDetail } from "../UserDetail";
import { useUserContext } from "../../contexts/UserContext";
import { pathOrNA } from "../../helpers/pathOrNA";
import { useNetwork } from "../../hooks/useNetwork";
import { dateToMMDDYY, getDate } from "../../common/date_utils";
import { UserInvitationsTree } from "./UserInvitationsTree";
import { UserMembershipRemoveButton } from "../UserMembershipRemoveButton";
import { useSelectedNetworkImportTypes } from "../../hooks/useSelectedNetworkImportType";
import { useSelectedNetworkContext } from "../../contexts/NetworkContext/context";
import useAuth from "../../routes/login/AuthContext";

const RESEND_MODAL_TOOLTIP_TEXT = `This user has already registered and an invitation link is no longer required. This user can access their membership by signing in with the social account (Facebook, Google, Apple) listed above under "Login method`;

const booleanIfElse = ifElse(Boolean, always("Yes"), always("No"));
const checkResidentStatus = ifElse(Boolean, always("Renewal"), always("New"));

const getRegistered = compose(booleanIfElse, pathOr(false, ["registered"]));
const getResidentStatus = compose(checkResidentStatus, pathOr(false, ["renewal"]));
const getVisibility = compose(booleanIfElse, pathOr(false, ["hidden"]));

export const MembershipDetailsTab = () => {
  const { goBack } = useHistory();
  const { selectedNetworkId } = useSelectedNetworkContext();
  const { isAcc, isOpen, isPasscode } = useSelectedNetworkImportTypes();
  const { user, isLoading, loadUser } = useUserContext();
  const { getL2, getL3, getL4 } = useNetwork();
  const { isSuperAdmin } = useAuth();

  const l2 = React.useMemo(() => user?.l1Id ? getL2(user?.l1Id, user?.l2Id) : undefined, [user?.l1Id, user?.l2Id, getL2]);
  const l3 = React.useMemo(() => user?.l1Id ? getL3(user?.l1Id, user?.l2Id, user?.l3Id) : undefined, [
    user?.l1Id,
    user?.l2Id,
    user?.l3Id,
    getL3,
  ]);
  const l4 = React.useMemo(() => user?.l1Id ? getL4(user?.l1Id, user?.l2Id, user?.l3Id, user?.l4Id) : undefined, [
    user?.l1Id,
    user?.l2Id,
    user?.l3Id,
    user?.l4Id,
    getL4,
  ]);

  const isNotImportDate = !selectedNetworkId || isOpen || isPasscode;

  // Define all fields with their priority order and data
  const fields = React.useMemo(() => {
    const allFields = [
      // Primary fields (priority 1-11 as specified in task)
      { priority: 1, label: "Network:", detail: l2?.l1Name ?? "N/A" },
      { priority: 2, label: "Subnetwork:", detail: l2?.l2Name ?? "N/A" },
      { priority: 3, label: "Unit Type:", detail: l3?.name ?? "N/A" },
      { priority: 4, label: "Room Type:", detail: l4?.name ?? "N/A" },
      { priority: 5, label: "Resident Status:", detail: getResidentStatus(user) },
      { priority: 6, label: "Gender:", detail: pathOrNA(["sex"], user) },
      { priority: 7, label: "Email:", detail: pathOrNA(["email"], user) },
      { priority: 8, label: "Hidden:", detail: getVisibility(user) },
      { priority: 9, label: "Registered:", detail: getRegistered(user) },
      { priority: 10, label: "Registration Date:", detail: user?.claimedDate ? dateToMMDDYY(getDate(user?.claimedDate)) : "N/A" },
      { priority: 11, label: "Membership id:", detail: pathOrNA(["membershipId"], user) },
      { priority: 12, label: "External Id:", detail: pathOrNA(["uniqueId"], user) },
      // Additional fields (lower priority)
      { priority: 13, label: "Lease Tracking Id:", detail: pathOrNA(["leaseTrackingId"], user) },
      { priority: 14, label: "Lease Id:", detail: pathOrNA(["leaseId"], user) },
      { priority: 15, label: "Application Id:", detail: pathOrNA(["applicationId"], user) },
    ];

    // Add Import Date conditionally
    if (!isNotImportDate) {
      allFields.push({
        priority: 16,
        label: "Import Date:",
        detail: user?.createdDate ? dateToMMDDYY(getDate(user?.createdDate)) : "N/A"
      });
    }

    // Sort fields: first by whether they have data (non-N/A), then by priority
    return allFields.sort((a, b) => {
      return a.priority - b.priority;
    });
  }, [user, l2, l3, l4, isNotImportDate]);

  return (
    <Grid container spacing={1} mt={1}>
      {/* Dynamically sorted fields */}
      {fields.map((field, index) => (
        <Grid item xs={12} key={`${field.label}-${index}`}>
          <UserDetail label={field.label} isLoading={isLoading} detail={field.detail} />
        </Grid>
      ))}

      {!!user && (
        <Grid item xs={12}>
          <UserInvitationsTree
            reloadUser={loadUser}
            invitations={user.invitations}
            userName={`${user.firstName} ${user.lastName}`}
            registered={user.registered}
          />
        </Grid>
      )}
      {
        user?.registered && (
          <Grid item xs={12}>
            <Alert severity="info">
              {RESEND_MODAL_TOOLTIP_TEXT}
            </Alert>
          </Grid>
        )
      }
      {!!user && (!isAcc || isSuperAdmin()) && (
        <Grid item xs={12}>
          <UserMembershipRemoveButton
            id={user.membershipId}
            onSuccess={goBack}
            label="Delete Membership"
            name={`${user.firstName} ${user.lastName}`}
          />
        </Grid>
      )}

    </Grid>
  );
};
